import type { TFunction } from 'i18next';

/**
 * Maps ad size dimensions to their descriptive labels
 */
export const AD_SIZE_LABELS = {
  '120x600': 'Skyscraper (120x600)',
  '160x600': 'Wide Skyscraper (160x600)',
  '180x150': 'Rectangle (180x150)',
  '200x200': 'Small Square (200x200)',
  '234x60': 'Half Banner (234x60)',
  '250x250': 'Square (250x250)',
  '300x50': 'Mobile Banner (300x50)',
  '300x250': 'Medium Rectangle (300x250)',
  '300x600': 'Half Page (300x600)',
  '320x50': 'Mobile Leaderboard (320x50)',
  '320x100': 'Large Mobile Banner (320x100)',
  '336x280': 'Large Rectangle (336x280)',
  '468x60': 'Banner (468x60)',
  '480x320': '480x320',
  '640x360': '16:9 – Standard Landscape (640x360)',
  '728x90': 'Leaderboard (728x90)',
  '970x90': 'Large Leaderboard (970x90)',
  '970x250': 'Billboard (970x250)',
  '1280x720': 'HD Landscape (1280x720)',
  '1920x1080': 'Full HD Landscape (1920x1080)',
  unmatched: 'Unmatched',
} as const;

/**
 * Explicit ordering of ad sizes to prevent accidental reordering
 */
export const AD_SIZE_LABELS_ORDER = [
  '640x360',
  '480x320',
  '468x60',
  '970x250',
  '1920x1080',
  '234x60',
  '300x600',
  '1280x720',
  '970x90',
  '320x100',
  '336x280',
  '728x90',
  '300x250',
  '300x50',
  '320x50',
  '180x150',
  '120x600',
  '200x200',
  '250x250',
  '160x600',
  'unmatched',
] as const;

/**
 * Returns the translated ad size label or falls back to the original size if no label exists
 */
export const getAdSizeLabel = (adSize: string, t: TFunction): string => {
  const label = AD_SIZE_LABELS[adSize];

  if (label) {
    return t(label);
  }

  return adSize;
};

/**
 * Custom sort function for ad sizes based on AD_SIZE_LABELS_ORDER
 */
export const sortAdSizeDataByLabel = <T extends { ad_size: string }>(
  data: T[],
  direction: 'asc' | 'desc' = 'asc'
): T[] => {
  return [...data].sort((a, b) => {
    const indexA = AD_SIZE_LABELS_ORDER.indexOf(a.ad_size as (typeof AD_SIZE_LABELS_ORDER)[number]);
    const indexB = AD_SIZE_LABELS_ORDER.indexOf(b.ad_size as (typeof AD_SIZE_LABELS_ORDER)[number]);

    // Items not in AD_SIZE_LABELS_ORDER go to the end
    const orderA = indexA === -1 ? Infinity : indexA;
    const orderB = indexB === -1 ? Infinity : indexB;

    if (direction === 'desc') {
      return orderB - orderA;
    }
    return orderA - orderB;
  });
};
